import * as React from "react";

import {
  useCustomerCartId,
  useCustomerInformationQuery,
  useCustomerSession,
} from "../../Common";

import {
  useDataMutableSourceShippingClientStorageData,
  useMyBagDataSourceIntersection,
  useReactMyBagLoginApp,
} from "../providers";

export const useMyPreviouslyFilledInInfo = () => {
  const shippingClientStorageData =
    useDataMutableSourceShippingClientStorageData();
  const customerInformation = useCustomerInformationQuery();
  const myBagDataSource = useMyBagDataSourceIntersection();
  const customerSession = useCustomerSession();
  const customerCartId = useCustomerCartId();
  const reactMyBagLoginApp = useReactMyBagLoginApp();

  const [hasInitialized, setHasInitialized] = React.useState(false);
  const [shouldMigrateDeliveryDetails, setShouldMigrateDeliveryDetails] =
    React.useState(false);
  const [lastMigratedEmail, setLastMigratedEmail] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    const {
      hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith,
      userLoggedInWithThisEmailLastIHeard,
    } = reactMyBagLoginApp;

    const { hasInitialized: shippingClientStorageData_hasInitialized } =
      shippingClientStorageData;

    const {
      status: customerInformationQueryStatus,
      data: customerInformationData,
    } = customerInformation || {};
    const customerInformation_hasInitialized =
      customerInformationQueryStatus === "success" ||
      customerInformationQueryStatus === "error";

    const { mostLatestType: myBagDataResponseType } = myBagDataSource;

    const { isCustomerLoggedIn } = customerSession;
    const { cartId } = customerCartId;

    const hasInitializedEverythingINeed =
      shippingClientStorageData_hasInitialized &&
      customerInformation_hasInitialized &&
      (myBagDataResponseType === "data" ||
        myBagDataResponseType === "error" ||
        (!cartId && !isCustomerLoggedIn));

    const theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting =
      userLoggedInWithThisEmailLastIHeard &&
      customerInformationData?.email === userLoggedInWithThisEmailLastIHeard;

    const hasMigratedAutofillsForAFreshUserLogin =
      lastMigratedEmail === userLoggedInWithThisEmailLastIHeard;

    const needsANewInitializationBecauseTheUserLoggedIn =
      !!hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith &&
      hasInitializedEverythingINeed &&
      theCustomerInformationHasLoadedForThisNewLoggedInUser_insteadOfWhateverStaleWasTherePreexisting &&
      !hasMigratedAutofillsForAFreshUserLogin;
    const needsAFirstTimeInitialization =
      hasInitializedEverythingINeed && !hasInitialized;

    if (
      needsANewInitializationBecauseTheUserLoggedIn ||
      needsAFirstTimeInitialization
    ) {
      setHasInitialized(true);
      setShouldMigrateDeliveryDetails(true);
    }
  }, [
    reactMyBagLoginApp.hasTheUserLoggedInWhileUsingThisPage_andWereInitiallyNotLoggedInToStartWith,
    reactMyBagLoginApp.userLoggedInWithThisEmailLastIHeard,
    shippingClientStorageData.hasInitialized,
    customerInformation.status,
    customerInformation.data,
    myBagDataSource.mostLatestType,
    customerSession.isCustomerLoggedIn,
    customerCartId.cartId,
    lastMigratedEmail,
    shouldMigrateDeliveryDetails,
    hasInitialized,
  ]);

  const handleMigrationComplete = React.useCallback(
    (theLastMigratedEmail?: typeof lastMigratedEmail) => {
      setLastMigratedEmail(theLastMigratedEmail ? theLastMigratedEmail : null);
      setShouldMigrateDeliveryDetails(false);
    },
    []
  );

  return {
    shouldMigrateDeliveryDetails,
    handleMigrationComplete,
  };
};
